import { ONE_MINUTE, addEventListeners, findLast, ExperimentalFeature, isExperimentalFeatureEnabled } from '@datadog/browser-core';
import { createPerformanceObservable, RumPerformanceEntryType } from '../../../browser/performanceObservable';
import { findLcpResourceEntry, getNavigationEntry, sanitizeFirstByte } from '../../../browser/performanceUtils';
import { getSelectorFromElement } from '../../getSelectorFromElement';
// Discard LCP timings above a certain delay to avoid incorrect data
// It happens in some cases like sleep mode or some browser implementations
export var LCP_MAXIMUM_DELAY = 10 * ONE_MINUTE;
/**
 * Track the largest contentful paint (LCP) occurring during the initial View.  This can yield
 * multiple values, only the most recent one should be used.
 * Documentation: https://web.dev/lcp/
 * Reference implementation: https://github.com/GoogleChrome/web-vitals/blob/master/src/onLCP.ts
 */
export function trackLargestContentfulPaint(configuration, firstHidden, eventTarget, callback) {
  // Ignore entries that come after the first user interaction. According to the documentation, the
  // browser should not send largest-contentful-paint entries after a user interact with the page,
  // but the web-vitals reference implementation uses this as a safeguard.
  var firstInteractionTimestamp = Infinity;
  var _addEventListeners = addEventListeners(configuration, eventTarget, ["pointerdown" /* DOM_EVENT.POINTER_DOWN */, "keydown" /* DOM_EVENT.KEY_DOWN */], function (event) {
      firstInteractionTimestamp = event.timeStamp;
    }, {
      capture: true,
      once: true
    }),
    stopEventListener = _addEventListeners.stop;
  var biggestLcpSize = 0;
  var performanceLcpSubscription = createPerformanceObservable(configuration, {
    type: RumPerformanceEntryType.LARGEST_CONTENTFUL_PAINT,
    buffered: true
  }).subscribe(function (entries) {
    var lcpEntry = findLast(entries, function (entry) {
      return entry.entryType === RumPerformanceEntryType.LARGEST_CONTENTFUL_PAINT && entry.startTime < firstInteractionTimestamp && entry.startTime < firstHidden.timeStamp && entry.startTime < LCP_MAXIMUM_DELAY &&
      // Ensure to get the LCP entry with the biggest size, see
      // https://bugs.chromium.org/p/chromium/issues/detail?id=1516655
      entry.size > biggestLcpSize;
    });
    if (lcpEntry) {
      var lcpTargetSelector;
      if (lcpEntry.element) {
        lcpTargetSelector = getSelectorFromElement(lcpEntry.element, configuration.actionNameAttribute);
      }
      var resourceUrl = computeLcpEntryUrl(lcpEntry);
      var lcpValue = lcpEntry.startTime;
      var subParts = isExperimentalFeatureEnabled(ExperimentalFeature.LCP_SUBPARTS) ? computeLcpSubParts(resourceUrl, lcpValue) : undefined;
      callback({
        value: lcpValue,
        targetSelector: lcpTargetSelector,
        resourceUrl: resourceUrl,
        subParts: subParts
      });
      biggestLcpSize = lcpEntry.size;
    }
  });
  return {
    stop: function stop() {
      stopEventListener();
      performanceLcpSubscription.unsubscribe();
    }
  };
}
// The property url report an empty string if the value is not available, we shouldn't report it in this case.
function computeLcpEntryUrl(entry) {
  return entry.url === '' ? undefined : entry.url;
}
/**
 * Compute the LCP sub-parts breakdown (loadDelay, loadTime, renderDelay).
 * Returns undefined if navigation timing data or TTFB is unavailable.
 */
function computeLcpSubParts(resourceUrl, lcpValue) {
  var firstByte = sanitizeFirstByte(getNavigationEntry());
  if (firstByte === undefined) {
    return undefined;
  }
  var lcpResourceEntry = resourceUrl ? findLcpResourceEntry(resourceUrl, lcpValue) : undefined;
  var lcpRequestStart = lcpResourceEntry ? Math.max(firstByte, lcpResourceEntry.requestStart || lcpResourceEntry.startTime) : firstByte;
  // Cap at LCP time to handle resources that continue downloading after LCP (e.g., videos)
  var lcpResponseEnd = Math.min(lcpValue, Math.max(lcpRequestStart, (lcpResourceEntry === null || lcpResourceEntry === void 0 ? void 0 : lcpResourceEntry.responseEnd) || 0));
  return {
    loadDelay: lcpRequestStart - firstByte,
    loadTime: lcpResponseEnd - lcpRequestStart,
    renderDelay: lcpValue - lcpResponseEnd
  };
}